// ============================================================================
// HOOK: useAdminDashboard — Estado e lógica do painel administrativo
// ARQUIVO: src/hooks/useAdminDashboard_v2.js
// RESPONSABILIDADE: Centralizar useState/useEffect/useRef do AdminDashboard.jsx
// ============================================================================

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { readQueuedPublishedDocumentEdit, publishedDocumentEditEventName } from '../utils/publishedDocumentEditor';

export function useAdminDashboard() {
  // ── Tabs principais ─────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('rda_admin_active_tab') || 'home');
  const [docSubTab, setDocSubTab] = useState('CONTRACTS');
  const [triageSubTab, setTriageSubTab] = useState(() => localStorage.getItem('rda_admin_triage_sub_tab') || 'INGESTION_HUB');

  // ── Query param navigation ──────────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const pageParam = searchParams.get('page');

  useEffect(() => {
    if (pageParam) {
      setActiveTab(pageParam);
    }
  }, [pageParam]);

  // ── MFA ─────────────────────────────────────────────────────────────────
  const [showMFA, setShowMFA] = useState(false);

  // ── Prefill / navegação entre módulos ───────────────────────────────────
  const [clientPrefillRequest, setClientPrefillRequest] = useState(null);

  // ── Preview de cliente (portal) ─────────────────────────────────────────
  const [previewClientId, setPreviewClientId] = useState(null);
  const [previewClientName, setPreviewClientName] = useState(null);

  // ── Computed ────────────────────────────────────────────────────────────
  const isFullBleedTab = ['docs', 'documents', 'contracts', 'procurations', 'declarations', 'termo_renuncia', 'termo_revogacao', 'petitions', 'informed_consents', 'office_uploads', 'prontuario_global', 'triage_hub', 'portal_inbox', 'alert_inbox', 'whatsapp_forensics', 'deadline_calculator', 'file_hub'].includes(activeTab);

  // ── Efeitos de tab legacy / validação ────────────────────────────────────
  useEffect(() => {
    const legacyMap = {
      curadoria_ia: 'academy_research',
      zotero_hub: 'academy_research',
      library: 'academy_research',
      docs: 'documents',
    };
    const allowed = new Set([
      'home', 'academy_admin', 'academy_research', 'academy_studio', 'docs', 'documents', 'contracts',
      'procurations', 'declarations', 'termo_renuncia', 'termo_revogacao', 'petitions', 'informed_consents', 'office_uploads', 'triage_hub',
      'portal_inbox', 'alert_inbox', 'office_settings', 'surgeon_hub', 'clientes', 'prontuario_global',
      'processos', 'financeiro', 'financial_manager', 'personal_finance', 'recta_finance',
      'legal_bi', 'investments', 'agenda', 'timesheet',
      'file_hub', 'whatsapp_forensics', 'deadline_calculator',
    ]);
    const migrated = legacyMap[activeTab] || activeTab;
    const safe = allowed.has(migrated) ? migrated : 'home';
    if (safe !== activeTab) setActiveTab(safe);
  }, [activeTab]);

  useEffect(() => { localStorage.setItem('rda_admin_active_tab', activeTab); }, [activeTab]);

  useEffect(() => { localStorage.setItem('rda_admin_triage_sub_tab', triageSubTab); }, [triageSubTab]);

  // ── Efeito de published document edit ──────────────────────────────────
  useEffect(() => {
    const applyEditRequest = (request) => {
      const targetTab = String(request?.tab || '').trim();
      if (targetTab === 'contracts' || targetTab === 'procurations') {
        setActiveTab(targetTab);
      }
    };
    applyEditRequest(readQueuedPublishedDocumentEdit());
    const handler = (event) => {
      const detail = event?.detail && typeof event.detail === 'object' ? event.detail : {};
      applyEditRequest(detail);
    };
    window.addEventListener(publishedDocumentEditEventName, handler);
    return () => window.removeEventListener(publishedDocumentEditEventName, handler);
  }, []);

  // ── Preview de cliente via evento customizado ──────────────────────────
  useEffect(() => {
    const handler = (e) => {
      setPreviewClientId(e?.detail?.clientId || null);
      setPreviewClientName(e?.detail?.clientName || null);
    };
    window.addEventListener('rda:preview-portal', handler);
    return () => window.removeEventListener('rda:preview-portal', handler);
  }, []);

  // ── Efeito de client prefill ───────────────────────────────────────────
  useEffect(() => {
    const payload = clientPrefillRequest?.data;
    if (typeof window === 'undefined') return;
    if (activeTab !== 'clientes' || !payload || typeof payload !== 'object') return;
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('rda:client-prefill', { detail: { prefillData: payload } }));
      setClientPrefillRequest(null);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [activeTab, clientPrefillRequest]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleCreateClientFromAI = (prefillData) => {
    const payload = prefillData && typeof prefillData === 'object' ? prefillData : null;
    if (!payload) return;
    setClientPrefillRequest({ data: payload, requestedAt: Date.now() });
    setActiveTab('clientes');
  };

  const handleOpenUploadCenter = () => {
    setActiveTab('office_uploads');
  };

  const handleDownloadLocalEngineInstaller = () => {
    try {
      const link = document.createElement('a');
      link.href = '/install_rda_engine.bat';
      link.download = 'install_rda_engine.bat';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      alert('Não foi possível iniciar o download do instalador do motor local.');
      console.error(e);
    }
  };

  // ── Header meta ──────────────────────────────────────────────────────────
  const headerMeta =
    activeTab === 'home' ? { title: 'Visão Geral', subtitle: 'Painel central com visão executiva e atalhos do escritório.' } :
    activeTab === 'academy_admin' ? { title: 'Gestão de Ensino', subtitle: 'Administre conteúdos, aulas e estrutura da área acadêmica.' } :
    activeTab === 'academy_research' ? { title: 'Pesquisa & Acervo', subtitle: 'Consulte materiais, referências e bases de apoio interno.' } :
    activeTab === 'docs' ? { title: 'Central de Documentos', subtitle: 'Acesse contratos, procurações, uploads e anexos do escritório.' } :
    activeTab === 'documents' ? { title: 'Contratos', subtitle: 'Produza, revise e publique contratos com o padrão do escritório.' } :
    activeTab === 'contracts' ? { title: 'Contratos', subtitle: 'Produza, revise e publique contratos com o padrão do escritório.' } :
    activeTab === 'procurations' ? { title: 'Procurações', subtitle: 'Produza, revise e publique procurações com o padrão do escritório.' } :
    activeTab === 'office_uploads' ? { title: 'Central de Uploads', subtitle: 'Acompanhe arquivos enviados e fluxos de entrada documental.' } :
    activeTab === 'portal_inbox' ? { title: 'Entradas do Portal', subtitle: 'Revise documentos e interações recebidas pelo portal do cliente.' } :
    activeTab === 'alert_inbox' ? { title: 'Central de Alertas', subtitle: 'Triagem de intimações OAB/PR e alertas da Mega Taxonomia Jurídica (RBDA/ANDA/Penal/Cível).' } :
    activeTab === 'triage_hub' ? { title: 'Sala de Ingestão', subtitle: 'SSOT de triagem para uploads do portal, GED órfão e vinculação operacional.' } :
    activeTab === 'whatsapp_forensics' ? { title: 'Perícia WhatsApp', subtitle: 'Importe, processe e vincule exportações do WhatsApp diretamente ao GED jurídico.' } :
    activeTab === 'prontuario_global' ? { title: 'Prontuário Global', subtitle: 'Acompanhe a linha do tempo consolidada de eventos do ecossistema.' } :
    activeTab === 'clientes' ? { title: 'Base de Clientes', subtitle: 'Gerencie cadastros, vínculos e dados oficiais dos clientes.' } :
    activeTab === 'processos' ? { title: 'Gestão Processual', subtitle: 'Visualize processos, andamentos e informações essenciais do contencioso.' } :
    activeTab === 'financeiro' ? { title: 'Caixa do Escritório', subtitle: 'Acompanhe receitas, recebimentos e movimentações financeiras do escritório.' } :
    activeTab === 'personal_finance' ? { title: 'Finanças Pessoais', subtitle: 'Controle lançamentos e visão financeira pessoal do titular.' } :
    activeTab === 'agenda' ? { title: 'Agenda & Prazos', subtitle: 'Organize compromissos, audiências, tarefas e vencimentos.' } :
    activeTab === 'timesheet' ? { title: 'Timesheet', subtitle: 'Registre e acompanhe horas e produtividade operacional.' } :
    activeTab === 'file_hub' ? { title: 'Processos e Casos', subtitle: 'Processos judiciais e casos pré-processuais do escritório — CNJ, movimentações e documentos organizados por cliente.' } :
    activeTab === 'office_settings' ? { title: 'Configurações do Escritório', subtitle: 'Ajuste preferências, parâmetros e integrações operacionais.' } :
    activeTab === 'surgeon_hub' ? { title: 'Cirurgia RDA', subtitle: 'Painel de refatoração via AST (porta 8010) — isolado do Acervo/WhatsApp.' } :
    activeTab === 'deadline_calculator' ? { title: 'Calculadora de Prazos', subtitle: 'Calcule dies a quo e dies ad quem com feriados de Curitiba/PR — CPC · CPP · JEC · CLT · MATERIAL.' } :
    activeTab === 'declarations' ? { title: 'Motor de Declarações', subtitle: 'Elabore declarações de hipossuficiência, qualificação e termos extrajudiciais.' } :
    activeTab === 'termo_renuncia' ? { title: 'Termo de Renúncia', subtitle: 'Renúncia do advogado ao mandato — art. 112 CPC e Código de Ética da OAB.' } :
    activeTab === 'termo_revogacao' ? { title: 'Termo de Revogação', subtitle: 'Revogação do mandato pelo cliente — art. 682, I, do Código Civil.' } :
    activeTab === 'petitions' ? { title: 'Motor de Petições', subtitle: 'Produza, edite e publique petições processuais com templates dinâmicos.' } :
    activeTab === 'informed_consents' ? { title: 'Consentimentos Informados', subtitle: 'Gere e gerencie termos de consentimento para peças processuais.' } :
    activeTab === 'academy_studio' ? { title: 'Estúdio', subtitle: 'Crie e organize aulas, módulos e conteúdo didático do escritório.' } :
    activeTab === 'financial_manager' ? { title: 'Painel Financeiro', subtitle: 'Visão geral de receitas, despesas e fluxo de caixa do escritório.' } :
    activeTab === 'recta_finance' ? { title: 'Recta Scripta — Financeiro', subtitle: 'Faturamento, boletos e controle de propostas comerciais via Recta Scripta.' } :
    activeTab === 'legal_bi' ? { title: 'Legal B.I.', subtitle: 'Business Intelligence do escritório — rentabilidade, KPIs e análise de dados.' } :
    activeTab === 'investments' ? { title: 'Projeto Milhão', subtitle: 'Painel de investimentos e gestão patrimonial do escritório.' } :
    { title: 'Gestão', subtitle: 'Painel administrativo do escritório.' };

  return {
    activeTab, setActiveTab,
    docSubTab, setDocSubTab,
    triageSubTab, setTriageSubTab,
    showMFA, setShowMFA,
    clientPrefillRequest, setClientPrefillRequest,
    previewClientId, setPreviewClientId,
    previewClientName, setPreviewClientName,
    isFullBleedTab,
    headerMeta,
    handleCreateClientFromAI,
    handleOpenUploadCenter,
    handleDownloadLocalEngineInstaller,
  };
}
